import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Ban,
  Mail,
  Eye,
  Users,
  Loader2,
  CalendarHeart,
  Baby,
  CreditCard,
  Gift,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient, Profile } from '@parentpath/supabase-client';
import { UserDetailModal } from './UserDetailModal';
import { PageHeader, LoadingSpinner, EmptyState } from '../../components/ui';
import { useAuth } from '../../features/auth';
import { formatPremiumSource, getPremiumGroup } from './utils/premiumSourceLabels';
import { callAdminUsers, describeBanError } from './utils/adminUsersClient';

const supabase = getSupabaseAdminClient();

// Loeschen von Nutzern laeuft ueber die Edge Function admin-users, weil dafuer
// die GoTrue-Admin-API gebraucht wird und die sich nicht ueber RLS abbilden
// laesst. Der Aufruf muss das JWT des eingeloggten Admins mitschicken.
//
// getSupabaseAdminClient() liefert aktuell denselben Client - die getrennte
// Konstante steht hier trotzdem, weil sie die Absicht festhaelt und
// unveraendert richtig bleibt, wenn die deprecated Funktion spaeter entfernt
// wird.
// Das Wort, das der Admin zur Bestaetigung tippen muss.
const DELETE_CONFIRMATION = 'LOESCHEN';

// Uebersetzt die englischen Fehler aus admin-users bzw. aus der DB-Funktion
// admin_delete_user_data in eine Meldung, die im Panel etwas aussagt. Die
// beiden fachlichen Faelle - Selbstloeschung (Edge Function) und Admin-Account
// (RAISE EXCEPTION in admin_delete_user_data, kommt als "Failed to delete user
// data: Cannot delete admin users" durch) - sollen nicht als generischer
// Fehler enden.
export function describeDeleteError(message: string): string {
  const normalized = message.toLowerCase();

  if (normalized.includes('cannot delete your own account')) {
    return 'Du kannst deinen eigenen Account nicht loeschen. Das muss ein anderer Admin uebernehmen.';
  }
  if (normalized.includes('cannot delete admin users')) {
    return 'Dieser Account hat Admin-Rechte und kann nicht geloescht werden. Die Admin-Rolle muss vorher in der Datenbank entzogen werden.';
  }
  if (normalized.includes('access denied') || normalized.includes('admin privileges')) {
    return 'Keine Berechtigung: Zum Loeschen werden Admin-Rechte benoetigt.';
  }
  if (normalized.includes('unauthorized') || normalized.includes('no authorization header')) {
    return 'Die Sitzung ist abgelaufen. Bitte neu anmelden und es noch einmal versuchen.';
  }
  if (normalized.includes('valid user_id')) {
    return 'Ungueltige Benutzer-ID - der Account konnte nicht zugeordnet werden.';
  }
  if (normalized.includes('not configured')) {
    return 'Die Edge Function admin-users ist nicht vollstaendig konfiguriert. Bitte im Backend pruefen.';
  }

  return `Der Account konnte nicht geloescht werden: ${message}`;
}

type UserRole = 'all' | 'pregnant' | 'partner';
type UserStatus = 'all' | 'active' | 'banned';

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole>('all');
  const [statusFilter, setStatusFilter] = useState<UserStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<Profile | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const pageSize = 10;
  const queryClient = useQueryClient();
  const { user: currentAdmin } = useAuth();

  // Fetch users with pagination
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', currentPage, searchQuery, roleFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' });

      // Apply search filter
      if (searchQuery) {
        query = query.or(`email.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%`);
      }

      // Apply role filter
      if (roleFilter === 'pregnant') {
        query = query.eq('user_role', 'pregnant');
      } else if (roleFilter === 'partner') {
        query = query.eq('user_role', 'partner');
      }

      // Apply status filter
      if (statusFilter === 'active') {
        query = query.eq('is_banned', false);
      } else if (statusFilter === 'banned') {
        query = query.eq('is_banned', true);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        users: data as Profile[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch pregnancy data for users (calculate current week from expected_due_date).
  // Die SSW selbst steht nur noch im Detail-Modal - hier entscheidet sie
  // darueber, ob in der Modus-Spalte das Schwangerschafts-Icon erscheint.
  const { data: pregnancyData } = useQuery({
    queryKey: ['user-pregnancies', usersData?.users?.map(u => u.id)],
    enabled: !!usersData?.users?.length,
    queryFn: async () => {
      const userIds = usersData!.users.map(u => u.id);

      const { data, error } = await supabase
        .from('pregnancies')
        .select('created_by, expected_due_date')
        .in('created_by', userIds)
        .eq('is_active', true);

      if (error) throw error;

      const pregnancyMap: Record<string, number | null> = {};
      (data as { created_by: string; expected_due_date: string }[])?.forEach(p => {
        // Calculate current week from due date (40 weeks total)
        const dueDate = new Date(p.expected_due_date);
        const now = new Date();
        const weeksRemaining = Math.ceil((dueDate.getTime() - now.getTime()) / (7 * 24 * 60 * 60 * 1000));
        const currentWeek = 40 - weeksRemaining;
        pregnancyMap[p.created_by] = Math.max(1, Math.min(40, currentWeek));
      });

      return pregnancyMap;
    },
  });

  // Fetch completed pregnancies with birth (for baby mode detection)
  const { data: babyModeData } = useQuery({
    queryKey: ['user-baby-mode', usersData?.users?.map(u => u.id)],
    enabled: !!usersData?.users?.length,
    queryFn: async () => {
      const userIds = usersData!.users.map(u => u.id);

      const { data, error } = await supabase
        .from('pregnancies')
        .select('created_by')
        .in('created_by', userIds)
        .eq('completion_type' as never, 'birth');

      if (error) return new Set<string>();

      const babyModeSet = new Set<string>();
      (data as { created_by: string }[])?.forEach(p => {
        babyModeSet.add(p.created_by);
      });

      return babyModeSet;
    },
  });

  const totalPages = Math.ceil((usersData?.totalCount || 0) / pageSize);

  // Ban/Unban mutation
  //
  // Laeuft ueber die Edge Function, nicht mehr per UPDATE auf profiles: die
  // Sperre muss zusaetzlich in auth.users gesetzt werden, sonst blockiert sie
  // nichts. Ausserdem verbietet die Datenbank das direkte Beschreiben der
  // Sperrspalten seit Migration 168.
  const banMutation = useMutation({
    mutationFn: async ({ userId, shouldBan }: { userId: string; shouldBan: boolean }) => {
      await callAdminUsers<{ success: boolean; user_id: string; banned: boolean }>({
        action: 'ban',
        user_id: userId,
        banned: shouldBan,
      });
    },
    onSuccess: (_, { shouldBan }) => {
      toast.success(shouldBan ? 'Benutzer wurde gesperrt' : 'Benutzer wurde entsperrt');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(describeBanError(error.message));
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      // Nutzdaten (admin_delete_user_data) und Auth-User werden serverseitig
      // in der Edge Function geloescht, in dieser Reihenfolge.
      await callAdminUsers<{ success: boolean; user_id: string }>({
        action: 'delete',
        user_id: userId,
      });
    },
    onSuccess: () => {
      toast.success('Benutzer wurde geloescht');
      closeDeleteDialog();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(describeDeleteError(error.message), { duration: 8000 });
    },
  });

  const openDeleteDialog = (user: Profile) => {
    setUserToDelete(user);
    setDeleteConfirmation('');
  };

  const closeDeleteDialog = () => {
    setUserToDelete(null);
    setDeleteConfirmation('');
  };

  // Send email function - oeffnet das Mailprogramm des Admins
  const handleSendEmail = (user: Profile) => {
    const subject = encodeURIComponent('ParentPath Support');
    const body = encodeURIComponent(`Hallo ${user.display_name || 'Benutzer'},\n\n`);
    window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Die beiden Faelle, die serverseitig ohnehin abgelehnt werden, schon im
  // Dialog abfangen - dann bleibt der Fehlerpfad die Ausnahme.
  const deleteBlockedReason = !userToDelete
    ? null
    : userToDelete.id === currentAdmin?.id
    ? 'Das ist dein eigener Account. Selbstloeschung lehnt der Server ab - das muss ein anderer Admin uebernehmen.'
    : userToDelete.role === 'admin'
    ? 'Dieser Account hat Admin-Rechte. Die Datenbank verhindert das Loeschen von Admins - die Rolle muss vorher entzogen werden.'
    : null;

  const canConfirmDelete =
    !!userToDelete &&
    !deleteBlockedReason &&
    deleteConfirmation === DELETE_CONFIRMATION &&
    !deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={Users}
        iconBg="bg-teal-100"
        iconColor="text-teal-600"
        title="Benutzerverwaltung"
        description="Alle registrierten Benutzer verwalten"
      />

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach E-Mail oder Name suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as UserRole);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="all">Alle Rollen</option>
              <option value="pregnant">Schwangere</option>
              <option value="partner">Partner</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as UserStatus);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="banned">Gesperrt</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">E-Mail</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Premium</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Modus</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="py-12">
                    <div className="flex justify-center">
                      <LoadingSpinner size="lg" />
                    </div>
                  </td>
                </tr>
              ) : usersData?.users?.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      icon={Users}
                      title="Keine Benutzer gefunden"
                      description={searchQuery ? 'Versuche einen anderen Suchbegriff' : undefined}
                    />
                  </td>
                </tr>
              ) : (
                usersData?.users?.map((user) => {
                  const pregnancyWeek = pregnancyData?.[user.id];
                  const premiumGroup = getPremiumGroup(user);

                  return (
                    <tr key={user.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-900">{user.email}</span>
                      </td>
                      <td className="py-3 px-4">
                        {premiumGroup === 'paid' ? (
                          <span
                            className="inline-flex text-amber-500"
                            title={`Premium bezahlt - ${formatPremiumSource(user)}`}
                          >
                            <CreditCard className="w-4 h-4" />
                          </span>
                        ) : premiumGroup === 'code' ? (
                          <span
                            className="inline-flex text-violet-500"
                            title={`Premium per Code - ${formatPremiumSource(user)}`}
                          >
                            <Gift className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="text-slate-400" title="Kein Premium">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {babyModeData?.has(user.id) ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-blue-600" title="Baby-Modus">
                            <Baby className="w-4 h-4" />
                          </span>
                        ) : pregnancyWeek ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-rose-500" title="Schwangerschaft">
                            <CalendarHeart className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedUserId(user.id)}
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                            title="Details anzeigen"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleSendEmail(user)}
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                            title="E-Mail schreiben"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => banMutation.mutate({ userId: user.id, shouldBan: !user.is_banned })}
                            disabled={banMutation.isPending}
                            className={`p-1.5 hover:bg-slate-100 rounded transition-colors ${
                              user.is_banned
                                ? 'text-emerald-500 hover:text-emerald-600'
                                : 'text-slate-400 hover:text-rose-500'
                            }`}
                            title={user.is_banned ? 'Entsperren' : 'Sperren'}
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteDialog(user)}
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-rose-500"
                            title="Account loeschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, usersData?.totalCount || 0)} von {usersData?.totalCount?.toLocaleString('de-DE') || 0}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 border rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-teal-500 text-white border-teal-500'
                        : 'border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="px-2 py-1.5 text-slate-400">...</span>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}

      {/* Delete User Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Account loeschen</h2>
              <p className="text-slate-600 mb-4">
                <strong>{userToDelete.display_name || userToDelete.email}</strong> wird mit allen
                Nutzdaten unwiderruflich geloescht. Diese Aktion kann nicht rueckgaengig gemacht
                werden.
              </p>

              {deleteBlockedReason ? (
                <div className="flex gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{deleteBlockedReason}</p>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Zum Bestaetigen <strong>{DELETE_CONFIRMATION}</strong> eintippen
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    // Einfuegen bewusst unterbunden: die Bestaetigung soll
                    // wirklich getippt werden. onPaste deckt Strg+V und das
                    // Kontextmenue ab, onDrop das Ziehen von Text ins Feld.
                    onPaste={(e) => {
                      e.preventDefault();
                      toast.error('Bitte tippe das Wort - Einfuegen ist hier nicht moeglich.');
                    }}
                    onDrop={(e) => e.preventDefault()}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={DELETE_CONFIRMATION}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={closeDeleteDialog}
                  className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => deleteMutation.mutate(userToDelete.id)}
                  disabled={!canConfirmDelete}
                  className="flex-1 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Loeschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
